import { SimulationRecord, deleteSimulation } from "../lib/simulationsStore";
import { CalculatorInputs } from "../types";
import { Trash2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { maskNIF } from "../utils/privacy";
import { useState } from "react";

interface Props {
  simulations: SimulationRecord[];
  onLoad: (inputs: CalculatorInputs, id: string) => void;
  onUpdate: () => void;
}

export default function SimulationsList({
  simulations,
  onLoad,
  onUpdate,
}: Props) {
  const { user } = useAuth();
  const [revealedNifs, setRevealedNifs] = useState<Set<string>>(new Set());

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar esta simulación?")) return;
    await deleteSimulation(id, user);
    onUpdate();
  };

  const handleLoad = (inputs: CalculatorInputs, id: string) => {
    onLoad(inputs, id);
  };

  const toggleNifReveal = (id: string) => {
    setRevealedNifs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const fmt = (n: number) =>
    n.toLocaleString("es-ES", { maximumFractionDigits: 0 });

  return (
    <div
      style={{
        marginTop: "24px",
        backgroundColor: "#fff",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow)",
        padding: "24px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 4px",
            }}
          >
            Simulaciones guardadas
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "12px",
              color: "var(--muted)",
            }}
          >
            {user ? 'Guardadas en tu cuenta' : 'Guardadas localmente'}
          </p>
        </div>
        <span
          style={{
            fontSize: "12px",
            color: "var(--muted)",
            padding: "4px 10px",
            backgroundColor: "var(--bg)",
            borderRadius: "999px",
            fontWeight: 600,
          }}
        >
          {simulations.length} {simulations.length === 1 ? "registro" : "registros"}
        </span>
      </div>

      {simulations.length === 0 ? (
        <p
          style={{
            fontSize: "13px",
            color: "var(--muted)",
            textAlign: "center",
            padding: "32px 0",
          }}
        >
          Todavía no has guardado simulaciones.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#fafbfc",
                  borderBottom: "2px solid var(--border)",
                }}
              >
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "var(--text)",
                  }}
                >
                  Fecha
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "var(--text)",
                  }}
                >
                  Simulación
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "var(--text)",
                  }}
                >
                  NIF
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "10px 12px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "var(--text)",
                  }}
                >
                  Bruto
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "10px 12px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "var(--text)",
                  }}
                >
                  Neto
                </th>
                <th
                  style={{
                    textAlign: "center",
                    padding: "10px 12px",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "var(--text)",
                  }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {simulations.map((sim, idx) => (
                <tr
                  key={sim.id}
                  style={{
                    borderBottom: "1px solid var(--border)",
                    backgroundColor: idx % 2 === 0 ? "transparent" : "#fafbfc",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f1f5f9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      idx % 2 === 0 ? "transparent" : "#fafbfc")
                  }
                >
                  <td style={{ padding: "12px" }}>
                    <div style={{ fontSize: "13px", color: "var(--text)" }}>
                      {new Date(sim.createdAt).toLocaleDateString("es-ES", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--muted)" }}>
                      {new Date(sim.createdAt).toLocaleTimeString("es-ES", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </td>
                  <td style={{ padding: "12px", fontWeight: 600 }}>
                    {sim.clientName}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      color: "var(--muted)",
                      fontFamily: "monospace",
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>
                        {sim.clientNif && revealedNifs.has(sim.id) ? sim.clientNif : maskNIF(sim.clientNif)}
                      </span>
                      {sim.clientNif && sim.clientNif.length > 4 && (
                        <button
                          onClick={() => toggleNifReveal(sim.id)}
                          style={{
                            appearance: 'none',
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            color: 'var(--muted)',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title={revealedNifs.has(sim.id) ? 'Ocultar NIF' : 'Mostrar NIF'}
                        >
                          {revealedNifs.has(sim.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      fontWeight: 600,
                    }}
                  >
                    {fmt(sim.brutoFacturado)} €
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      fontWeight: 700,
                      color: "var(--brand-ink)",
                    }}
                  >
                    {fmt(sim.netoFinalTrasRenta)} €
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => handleLoad(sim.inputs, sim.id)}
                        style={{
                          appearance: "none",
                          background: "var(--brand)",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 12px",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#fff",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--brand-ink)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--brand)";
                        }}
                      >
                        Cargar
                      </button>
                      <button
                        onClick={() => handleDelete(sim.id)}
                        style={{
                          appearance: "none",
                          background: "none",
                          border: "1px solid var(--border)",
                          borderRadius: "6px",
                          padding: "6px 8px",
                          fontSize: "11px",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          display: "flex",
                          alignItems: "center",
                          color: "var(--muted)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#fee";
                          e.currentTarget.style.borderColor = "#fcc";
                          e.currentTarget.style.color = "#dc2626";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.borderColor = "var(--border)";
                          e.currentTarget.style.color = "var(--muted)";
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
